import type { ChartTheme } from 'openalgo-charts';
import type { DrawingDocumentStore, DrawingsDocument } from 'openalgo-charts/draw';
import type { Widget } from 'openalgo-charts/widget';
import { defaultSessionShade, type SessionShade } from './savedChart';

interface WatermarkChart {
  setWatermarkOptions?: (options: boolean | { visible?: boolean; text?: string }) => void;
  watermarkOptions?: () => { visible?: boolean; text?: string; color?: string; opacity?: number; fontSize?: number };
}

/**
 * Blank text follows the chart symbol and the interval.
 * A watermark the user hid stays hidden.
 */
export function bindChartWatermark(chart: WatermarkChart): void {
  let current: ReturnType<NonNullable<WatermarkChart['watermarkOptions']>> = {};
  try {
    current = chart.watermarkOptions?.() ?? {};
  } catch {
    current = {};
  }
  if (current.visible === false) return;
  try {
    chart.setWatermarkOptions?.({ ...current, visible: true, text: '' });
  } catch {
    // The chart was destroyed.
  }
}

function cloneJson(value: unknown): unknown {
  try {
    return JSON.parse(JSON.stringify(value)) as unknown;
  } catch {
    return value;
  }
}

/**
 * Drawings the widget keeps per instrument. `getState` only carries the symbol
 * on screen, so the other symbols live in this store.
 */
export function createDrawingStore(
  initial: Record<string, unknown>,
  onChange: (docs: Record<string, unknown>) => void,
): DrawingDocumentStore {
  const docs: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(initial)) docs[key] = cloneJson(value);
  const publish = (): void => {
    onChange({ ...docs });
  };
  return {
    get(key: string): unknown {
      return Object.prototype.hasOwnProperty.call(docs, key) ? cloneJson(docs[key]) : null;
    },
    set(key: string, document: DrawingsDocument): boolean {
      const next = cloneJson(document);
      if (JSON.stringify(docs[key]) === JSON.stringify(next)) return true;
      docs[key] = next;
      publish();
      return true;
    },
    remove(key: string): void {
      if (!Object.prototype.hasOwnProperty.call(docs, key)) return;
      delete docs[key];
      publish();
    },
  };
}

/** The symbol box commits on Enter and then blurs. Keep it focused with the text selected. */
export function armSymbolField(root: ParentNode): () => void {
  const stops: Array<() => void> = [];
  root.querySelectorAll<HTMLInputElement>('.oac-sym__input').forEach((input) => {
    if (input.dataset.qfTicker === 'symbol') return;
    input.dataset.qfTicker = 'symbol';
    const onInput = (): void => {
      const next = input.value.toUpperCase();
      if (next === input.value) return;
      const pos = input.selectionStart ?? next.length;
      input.value = next;
      try {
        input.setSelectionRange(pos, pos);
      } catch {
        // A detached field cannot move the caret. The value is already uppercased.
      }
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'Enter') return;
      if (!input.value.trim()) return;
      requestAnimationFrame(() => {
        input.focus();
        input.select();
      });
    };
    input.addEventListener('input', onInput, true);
    input.addEventListener('keydown', onKey, true);
    stops.push(() => {
      input.removeEventListener('input', onInput, true);
      input.removeEventListener('keydown', onKey, true);
    });
  });
  return () => {
    for (const stop of stops) stop();
  };
}

/**
 * The library strokes the crosshair whenever a color is set. Transparent is
 * the off switch. Turning it back on restores the color it had.
 */
export function applyCrosshair(
  widget: Widget,
  theme: ChartTheme,
  on: boolean,
  blanked: { current: string | null },
): void {
  const themeColor = typeof theme.crosshair === 'string' ? theme.crosshair : 'rgba(0,0,0,0)';
  const paintCanvas = (color: string): void => {
    const setCanvas = widget.chart.setCanvasOptions;
    if (typeof setCanvas !== 'function') return;
    try {
      setCanvas.call(widget.chart, { crosshair: { color } });
    } catch {
      // Canvas options are optional on a test double.
    }
  };
  if (!on) {
    if (blanked.current == null) {
      let current: unknown;
      try {
        current = widget.chart.theme?.().crosshair;
      } catch {
        current = undefined;
      }
      blanked.current = typeof current === 'string' && current !== 'rgba(0,0,0,0)' ? current : themeColor;
    }
    try {
      widget.setTheme({ ...theme, crosshairLabelVisible: false, crosshair: 'rgba(0,0,0,0)' });
    } catch {
      // A destroyed widget.
    }
    paintCanvas('rgba(0,0,0,0)');
    return;
  }
  const restore = blanked.current;
  blanked.current = null;
  try {
    widget.setTheme(restore
      ? { ...theme, crosshairLabelVisible: true, crosshair: restore }
      : { ...theme, crosshairLabelVisible: true });
  } catch {
    // A destroyed widget.
  }
  if (restore) paintCanvas(restore);
}

/**
 * Our controls, right of the library's spacer. The symbol box stays first on
 * the left however many controls we add. A narrow bar scrolls sideways.
 */
function toolGroup(bar: HTMLElement): HTMLElement {
  const existing = bar.querySelector('.qf-chart-tools');
  if (existing instanceof HTMLElement) return existing;
  const group = document.createElement('span');
  group.className = 'qf-chart-tools';
  group.style.display = 'inline-flex';
  group.style.alignItems = 'center';
  group.style.flex = '0 0 auto';
  const spacer = bar.querySelector('.oac-topbar__spacer');
  if (spacer) spacer.after(group);
  else bar.append(group);
  return group;
}

function crosshairButton(bar: HTMLElement): HTMLButtonElement {
  const existing = bar.querySelector('.qf-chart-crosshair');
  if (existing instanceof HTMLButtonElement) return existing;
  const created = document.createElement('button');
  created.type = 'button';
  created.className = 'qf-chart-crosshair';
  created.textContent = 'Crosshair';
  created.style.display = 'inline-flex';
  created.style.alignItems = 'center';
  created.style.flex = '0 0 auto';
  created.style.margin = '0 4px';
  created.style.padding = '0 6px';
  created.style.border = '1px solid var(--oac-bd, #333)';
  created.style.background = 'transparent';
  created.style.color = 'currentColor';
  created.style.font = 'inherit';
  created.style.cursor = 'pointer';
  toolGroup(bar).prepend(created);
  return created;
}

export function paintCrosshairButton(root: HTMLElement, on: boolean, toggle: () => void): void {
  const bar = root.querySelector('.oac-topbar');
  if (!(bar instanceof HTMLElement)) return;
  const button = crosshairButton(bar);
  button.setAttribute('aria-pressed', on ? 'true' : 'false');
  button.title = on ? 'Turn the crosshair off' : 'Turn the crosshair on';
  button.style.opacity = on ? '1' : '0.45';
  button.onclick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    toggle();
  };
}

function sessionButtonStyle(button: HTMLButtonElement): void {
  button.type = 'button';
  button.style.display = 'inline-flex';
  button.style.alignItems = 'center';
  button.style.margin = '0';
  button.style.padding = '0 6px';
  button.style.border = '1px solid var(--oac-bd, #333)';
  button.style.background = 'transparent';
  button.style.color = 'currentColor';
  button.style.font = 'inherit';
  button.style.cursor = 'pointer';
}

/** Regular | Extended. Removed when this chart has no session callback. */
function sessionControl(bar: HTMLElement): HTMLElement {
  const existing = bar.querySelector('.qf-chart-session');
  if (existing instanceof HTMLElement) return existing;
  const wrap = document.createElement('span');
  wrap.className = 'qf-chart-session';
  wrap.setAttribute('role', 'group');
  wrap.setAttribute('aria-label', 'Session');
  wrap.style.display = 'inline-flex';
  wrap.style.alignItems = 'center';
  wrap.style.flex = '0 0 auto';
  wrap.style.margin = '0 4px';
  for (const [id, label] of [['regular', 'Regular'], ['extended', 'Extended']] as const) {
    const button = document.createElement('button');
    sessionButtonStyle(button);
    button.dataset.session = id;
    button.textContent = label;
    wrap.append(button);
  }
  const group = toolGroup(bar);
  const crosshair = group.querySelector('.qf-chart-crosshair');
  if (crosshair) crosshair.after(wrap);
  else group.append(wrap);
  return wrap;
}

/**
 * Regular | Extended.
 * Daily and higher leave the control in place and disable it. Those bars are whole sessions.
 */
export function paintSessionControl(
  root: HTMLElement,
  on: boolean,
  choose: ((extended: boolean) => void) | undefined,
  enabled = true,
): void {
  const bar = root.querySelector('.oac-topbar');
  if (!(bar instanceof HTMLElement)) return;
  if (!choose) {
    bar.querySelector('.qf-chart-session')?.remove();
    return;
  }
  const wrap = sessionControl(bar);
  const showingExtended = enabled && on;
  for (const button of wrap.querySelectorAll<HTMLButtonElement>('button[data-session]')) {
    const extended = button.dataset.session === 'extended';
    const pressed = extended === showingExtended;
    button.disabled = !enabled;
    button.setAttribute('aria-pressed', pressed ? 'true' : 'false');
    button.style.fontWeight = pressed ? '600' : '400';
    button.style.opacity = !enabled ? '0.35' : pressed ? '1' : '0.45';
    button.style.cursor = enabled ? 'pointer' : 'default';
    button.title = !enabled
      ? 'Daily bars stay on the regular session.'
      : extended
        ? 'Extended session. Intraday bars include premarket and after hours.'
        : 'Regular session, 09:30–16:00 ET.';
    button.onclick = (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (!enabled || pressed) return;
      choose(extended);
    };
  }
}

/** Named chart layouts. Sits with the other controls, right of the symbol box. */
export function paintLayoutsButton(root: HTMLElement, open: ((anchor: HTMLElement) => void) | null): void {
  const bar = root.querySelector('.oac-topbar');
  if (!(bar instanceof HTMLElement)) return;
  if (!open) {
    bar.querySelector('.qf-chart-layouts')?.remove();
    return;
  }
  const existing = bar.querySelector('.qf-chart-layouts');
  const button = existing instanceof HTMLButtonElement ? existing : document.createElement('button');
  if (!(existing instanceof HTMLButtonElement)) {
    button.className = 'qf-chart-layouts';
    button.textContent = 'Layouts';
    sessionButtonStyle(button);
    button.style.margin = '0 4px';
    const group = toolGroup(bar);
    const session = group.querySelector('.qf-chart-session');
    if (session) session.after(button);
    else group.append(button);
  }
  button.title = 'Save or open a chart layout. The ticker on this chart stays.';
  button.setAttribute('aria-haspopup', 'dialog');
  button.onclick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    open(button);
  };
}

function watermarkGroup(dialog: ParentNode): HTMLElement | null {
  for (const head of dialog.querySelectorAll('.oac-head')) {
    if (head.textContent?.trim() !== 'Watermark') continue;
    const group = head.nextElementSibling;
    if (group instanceof HTMLElement && group.classList.contains('oac-form__group')) return group;
  }
  return null;
}

/**
 * Color and opacity for the pre/post wash, beside the watermark.
 * The wash itself is on only while the session is Extended.
 */
export function fillSessionSettings(
  root: HTMLElement,
  getShade: () => SessionShade | null,
  onChange: (next: SessionShade) => void,
): void {
  const dialog = root.querySelector?.('.oac-settings');
  if (!(dialog instanceof HTMLElement)) return;
  const watermark = watermarkGroup(dialog);
  if (!watermark) return;
  let group = dialog.querySelector('[data-qf-session-shade]');
  if (!(group instanceof HTMLElement)) {
    const doc = root.ownerDocument ?? document;
    const head = doc.createElement('div');
    head.className = 'oac-head';
    head.textContent = 'Session shading';
    const built = doc.createElement('div');
    built.className = 'oac-form__group';
    built.dataset.qfSessionShade = '';
    built.style.display = 'flex';
    built.style.flexDirection = 'column';
    built.style.gap = '8px';
    built.style.padding = '8px 0 2px';
    built.style.fontSize = '12px';
    const note = doc.createElement('p');
    note.textContent = 'Shades premarket and after-hours bars while Session is Extended.';
    note.style.margin = '0';
    note.style.opacity = '0.75';
    const colorLabel = doc.createElement('label');
    colorLabel.style.display = 'flex';
    colorLabel.style.alignItems = 'center';
    colorLabel.style.gap = '8px';
    colorLabel.append(doc.createTextNode('Color'));
    const color = doc.createElement('input');
    color.type = 'color';
    color.dataset.qfShadeColor = '';
    color.addEventListener('input', () => {
      const base = getShade() ?? defaultSessionShade();
      onChange({ ...base, color: color.value });
    });
    colorLabel.append(color);
    const opacityLabel = doc.createElement('label');
    opacityLabel.style.display = 'flex';
    opacityLabel.style.alignItems = 'center';
    opacityLabel.style.gap = '8px';
    opacityLabel.append(doc.createTextNode('Opacity'));
    const opacity = doc.createElement('input');
    opacity.type = 'range';
    opacity.min = '0';
    opacity.max = '1';
    opacity.step = '0.01';
    opacity.dataset.qfShadeOpacity = '';
    opacity.addEventListener('input', () => {
      const base = getShade() ?? defaultSessionShade();
      onChange({ ...base, opacity: Number(opacity.value) });
    });
    opacityLabel.append(opacity);
    built.append(note, colorLabel, opacityLabel);
    watermark.after(head, built);
    group = built;
  }
  const look = getShade() ?? defaultSessionShade();
  const color = group.querySelector<HTMLInputElement>('[data-qf-shade-color]');
  if (color && document.activeElement !== color && color.value.toLowerCase() !== look.color) {
    color.value = look.color;
  }
  const opacity = group.querySelector<HTMLInputElement>('[data-qf-shade-opacity]');
  if (opacity && document.activeElement !== opacity) {
    const next = String(look.opacity);
    if (opacity.value !== next) opacity.value = next;
  }
}

/** Chart settings rebuilds its form when the tab changes. Fill the group each time it appears. */
export function watchSessionSettings(
  root: HTMLElement,
  getShade: () => SessionShade | null,
  onChange: (next: SessionShade) => void,
): () => void {
  const paint = (): void => {
    fillSessionSettings(root, getShade, onChange);
  };
  paint();
  const observer = new MutationObserver(paint);
  observer.observe(root, { childList: true, subtree: true });
  return () => observer.disconnect();
}
